import React from 'react';
import { useTranslation } from 'react-i18next';

import { Typography } from '../../../display/Typography';
import { useIsLandscapeTablet } from '../../../../hooks/useMediaBreakPoints';
import { MapComponent } from '../../../display/Map';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { PHOTO_GEOTAG_FLAG } from '../../../../utils/photoGeotagFlag';
import { pkFormatter } from '../../../../utils/transform';
import {
  directionKeyToShow,
  formatCoords,
  formatDistance,
  formatTakenAt,
  mapsUrl,
  type LocatableImage,
} from '../../../../utils/photoLocation';
import type { PkResolution } from '../../../../utils/resolvePkAtPosition';

import { usePhotoLocation } from './usePhotoLocation';
import { LocationMap, LocationSection, LocationTitle, MapsLink } from './styles';

const isOnline = () => typeof navigator === 'undefined' || navigator.onLine !== false;

/**
 * Bloque «Dónde se hizo» del visor a pantalla completa. Spec 2026-09-23 fotos
 * geolocalizadas, § 16.
 *
 * Como la galería del móvil, pero en el idioma de la carretera: primero la vía
 * y el PK (lo que el operario reconoce), luego el mapa, el origen de la foto,
 * la hora de la cámara y la precisión, y un enlace para abrirla en Maps.
 *
 * 🔴 Lo que NO enseña, a propósito (§ 16.2 D29): la dirección postal (Google
 * prohíbe en el EEE enseñarla junto a un mapa, y en carretera sirve de poco)
 * ni nada que junte las fotos de un operario.
 *
 * Solo con el flag `feature-photo-geotag`: sin él no existe y no pide nada.
 */
export const PhotoLocationPanel = ({
  image,
}: {
  image?: LocatableImage | null;
}): JSX.Element | null => {
  const { t } = useTranslation();
  const enabled = useFeatureFlag(PHOTO_GEOTAG_FLAG);
  const state = usePhotoLocation(image, enabled);
  // Mismos tamaños que «Fecha de creación» / «Hora de creación» (Definition):
  // S, y XS en la tablet en horizontal. Visto el 25/09: con otros tamaños el
  // bloque desentonaba del resto del panel.
  const size = useIsLandscapeTablet() ? 'XS' : 'S';

  if (state.status === 'off') return null;

  const line = (key: string, opts?: Record<string, unknown>) => (
    <Typography size={size}>{t(`modals:photoLocation.${key}`, opts)}</Typography>
  );

  let body: React.ReactNode;
  switch (state.status) {
    case 'loading':
      body = line('loading');
      break;
    case 'no-original':
      body = line('noOriginal');
      break;
    case 'offline':
      body = line('offline');
      break;
    case 'error':
      body = line('error');
      break;
    case 'no-gps':
      body = (
        <>
          {line('noGps')}
          {state.capture.roisCamera && line('noGpsRoisCamera')}
        </>
      );
      break;
    case 'ready': {
      const { capture, resolution } = state;
      const taken = formatTakenAt(capture.takenAtLocal);
      const meta = [
        t(`modals:photoLocation.${capture.roisCamera ? 'sourceRois' : 'sourceUpload'}`),
        taken,
        capture.accuracyMeters !== null
          ? t('modals:photoLocation.accuracy', { meters: Math.round(capture.accuracyMeters) })
          : null,
      ]
        .filter(Boolean)
        .join(' · ');
      body = (
        <>
          <RoadLine resolution={resolution} size={size} />
          {isOnline() && (
            <LocationMap>
              <MapComponent
                center={{ lat: capture.latitude, lng: capture.longitude }}
                point={{ lat: capture.latitude, lng: capture.longitude }}
                zoom={16}
                // Sin el punto azul de «dónde estoy yo»: lo que importa es
                // dónde se hizo la foto.
                geoloc={null}
                apiKey={import.meta.env.VITE_MAP_API_KEY}
                // Ampliar y recentrar, como el mapa del recorrido: en pequeño
                // no se distingue la carretera ni el entorno de la foto.
                allowFullscreen
              />
            </LocationMap>
          )}
          <Typography size={size}>{meta}</Typography>
          <Typography size={size}>{formatCoords(capture.latitude, capture.longitude)}</Typography>
          <MapsLink
            href={mapsUrl(capture.latitude, capture.longitude)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('modals:photoLocation.openInMaps')}
          </MapsLink>
        </>
      );
      break;
    }
  }

  return (
    <LocationSection aria-label={t('modals:photoLocation.title')}>
      {/* Como «Calidad de descarga», el otro apartado del panel. */}
      <LocationTitle>{t('modals:photoLocation.title')}</LocationTitle>
      {body}
    </LocationSection>
  );
};

/**
 * Carretera, PK y sentido; o «fuera de las carreteras» con la más cercana.
 * `out-of-range` se pinta como `ok`: aquí no se rellena ningún formulario, y
 * el PK sigue siendo la mejor referencia de dónde está la foto.
 */
const RoadLine = ({
  resolution,
  size,
}: {
  resolution: PkResolution | null;
  size: 'S' | 'XS';
}) => {
  const { t, i18n } = useTranslation();
  if (!resolution) return null;

  if (resolution.kind === 'ok' || resolution.kind === 'out-of-range') {
    const { roadName, pkKm, pkMeter, direction } = resolution.match;
    const dirKey = directionKeyToShow(direction);
    return (
      <Typography size={size} variant="semiBold">
        {t('modals:photoLocation.roadPk', { road: roadName, pk: pkFormatter(pkKm, pkMeter, true) })}
        {dirKey ? ` · ${t(dirKey)}` : ''}
      </Typography>
    );
  }

  return (
    <>
      <Typography size={size} variant="semiBold">
        {t('modals:photoLocation.outsideRoads')}
      </Typography>
      {resolution.kind === 'too-far' && (
        <Typography size={size}>
          {t('modals:photoLocation.nearestRoad', {
            road: resolution.nearest.roadName,
            // «a 10262 m» obliga a contar cifras; «a 10,3 km» se lee.
            distance: formatDistance(resolution.nearest.distance, i18n.language),
          })}
        </Typography>
      )}
    </>
  );
};

export default PhotoLocationPanel;
